import { useEffect, useState, useCallback, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore'
import { TeamsInterface, ProfileInterface } from 'ts-interfaces';

const teamsCache = new Map<string, TeamsInterface[]>();

export const useTeams = (
    designFirmID: string | undefined, 
    db: any | null,
    // Unused: members' permissions docs aren't read since the cutover (their
    // ACL arrays grant nothing). Kept for callers until PR6.
    _includePermissions: boolean = false,
    includeProfile: boolean = true
) => {
    const [teams, setTeams] = useState<TeamsInterface[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const isFetchingRef = useRef(false);
  
    const fetchTeams = useCallback(async () => {
        if (!designFirmID || !db) {
            setLoading(false);
            return; 
        }
        
        if (isFetchingRef.current) return;
        
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);
        
        try {
            // Check cache first
            const cacheKey = `${designFirmID}-${includeProfile}`;
            if (teamsCache.has(cacheKey)) {
                const cachedTeams = teamsCache.get(cacheKey);
                if (cachedTeams) {
                    setTeams(cachedTeams);
                    setLoading(false);
                    isFetchingRef.current = false;
                    console.log('Cache hit!');
                    return;
                }
            }
            
            // Fetch teams
            const teamsRef = collection(db, 'teams');
            const q = query(
                teamsRef, 
                where('designFirmID', '==', designFirmID), 
                where('status', '==', 'active')
            );
            const querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
                setTeams(null);
                setLoading(false);
                isFetchingRef.current = false;
                return;
            }

            // Get team data and extract UIDs (limit to 30 for 'in' query)
            const teamsData = querySnapshot.docs
                .slice(0, 30)
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as TeamsInterface));
            
            const uids = teamsData
                .map(team => team.uid)
                .filter((uid): uid is string => !!uid);

            // Batch fetch profiles if needed
            const enrichmentPromises: Promise<void>[] = [];
            const profilesMap = new Map<string, ProfileInterface>();

            if (uids.length > 0) {
                if (includeProfile) {
                    enrichmentPromises.push(
                        getDocs(query(collection(db, 'profiles'), where('uid', 'in', uids)))
                            .then(snapshot => {
                                //get length
                                snapshot.docs.forEach(doc => {
                                    const data = doc.data();
                                    profilesMap.set(data.uid, {
                                        id: doc.id,
                                        ...data
                                    } as ProfileInterface);
                                });
                            })
                            .catch(error => console.error('Failed to fetch profiles:', error))
                    );
                }

                await Promise.all(enrichmentPromises);
            }
            // Enrich team data with profiles
            const enrichedTeams = teamsData.map(team => {
                const enrichedTeam = { ...team } as any;
                
                if (team.uid) {
                    if (includeProfile) {
                        enrichedTeam.profile = profilesMap.get(team.uid);
                    }
                }
                
                return enrichedTeam as TeamsInterface;
            });

            teamsCache.set(cacheKey, enrichedTeams);
            setTeams(enrichedTeams);
            
        } catch (e) {
            console.error('Error fetching teams:', e);
            setError(e instanceof Error ? e : new Error('Failed to fetch teams'));
            setTeams(null);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [designFirmID, db, includeProfile]);

    useEffect(() => {
        fetchTeams();
    }, [fetchTeams]);
    return { 
        teams, 
        loading, 
        error
    };
}